import { useInfiniteQuery } from 'react-query';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  generateURL,
  generatePaginationQuery,
  FilterParameters,
  generateFilterExpression,
} from 'utilities/hooks/urlGeneration';
import { useWorkspaceContext } from 'hooks/common';
import { useIsConnectionValid } from 'components/CreateSpark/SparkBuilderDrawers/RightDrawer/forms/PluginFunctions/PluginConnectionAuthentication/utils';
import { fetchWrapper } from 'utils/fetchWrapper';
import type {
  PluginConnections,
  PluginConnectionsResponse,
} from 'types/PluginConnections';

const getPluginConnections = (
  pluginID: string | null | undefined,
  pageParam: number,
  workSpaceID: string | null | undefined
) => {
  if (!pluginID) {
    return Promise.reject('plugin id is required');
  }

  if (!workSpaceID) {
    return Promise.reject('workspace id is required');
  }

  const filters: FilterParameters[] = [
    {
      type: 'equal',
      key: 'pluginID',
      value: pluginID,
    },
    {
      type: 'equal',
      key: 'workspaceID',
      value: workSpaceID,
    },
  ];

  const queryArray = [
    generateFilterExpression('and', filters),
    generatePaginationQuery({
      pageNumber: pageParam,
      pageSize: 10,
    }),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS, queryArray);

  return fetchWrapper<PluginConnectionsResponse>({ url });
};

export const useInfiniteConnections = (pluginID: string, isOpen: boolean) => {
  const { currentWorkspaceID } = useWorkspaceContext();
  const checkConnectionValidity = useIsConnectionValid();
  const queryResult = useInfiniteQuery({
    queryKey: ['connections', pluginID],
    queryFn: ({ pageParam = 1 }) =>
      getPluginConnections(pluginID, pageParam, currentWorkspaceID),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.data?.length ? allPages?.length + 1 : undefined;
    },
    enabled: Boolean(pluginID && isOpen),
  });

  const { data } = queryResult;

  const connections = data?.pages.reduce<
    { pluginConnection: PluginConnections; isConnectionValid: boolean }[]
  >((acc, page) => {
    if (!page.data) {
      return acc;
    }
    const singlePluginConnection = page.data.map((pluginConnection) => {
      return {
        pluginConnection,
        isConnectionValid: checkConnectionValidity(
          pluginConnection.attributes.pluginConnectionStatusTypeID
        ),
      };
    });
    return [...acc, ...singlePluginConnection];
  }, []);

  return {
    connections,
    ...queryResult,
  };
};
